import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/AdminLayout";
import { api, errMsg, fmtDate } from "@/lib/api";

export default function Invites() {
  const [list, setList] = useState([]);
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [autoApprove, setAutoApprove] = useState(true);

  const load = () => api.get("/admin/invites").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/invites", { label, max_uses: Number(maxUses), auto_approve: autoApprove });
      toast.success(`Invite ${data.code} created`);
      setLabel("");
      setAutoApprove(true);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggle = async (code, patch) => {
    try {
      await api.patch(`/admin/invites/${code}`, patch);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const copy = (code) => {
    const link = `${window.location.origin}/register?code=${code}`;
    navigator.clipboard?.writeText(link).then(() => toast.success("Invite link copied"), () => toast.info(link));
  };

  return (
    <div>
      <PageHeader eyebrow="Access" title="Invite codes" />
      <form onSubmit={create} className="mb-8 grid grid-cols-1 items-end gap-4 rounded-lg border border-border bg-card p-6 sm:grid-cols-[1fr_160px_auto_auto]">
        <div className="space-y-1.5"><Label>Who is it for? (optional)</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. John from the gym" data-testid="invite-label-input" /></div>
        <div className="space-y-1.5"><Label>Max uses (0 = unlimited)</Label><Input type="number" min="0" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} data-testid="invite-max-uses-input" /></div>
        <label className="flex h-10 items-center gap-2 rounded-md border border-border px-3 text-sm">
          <Switch checked={autoApprove} onCheckedChange={setAutoApprove} data-testid="invite-auto-approve-switch" />Approve automatically
        </label>
        <Button type="submit" className="gap-2" data-testid="invite-create-button"><Plus className="h-4 w-4" />Generate code</Button>
      </form>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>For</TableHead><TableHead>Used</TableHead><TableHead>Created</TableHead><TableHead>Auto-approve</TableHead><TableHead>Active</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {list.map((i) => (
              <TableRow key={i.code} data-testid={`invite-row-${i.code}`}>
                <TableCell className="font-mono-data text-base tracking-widest text-emerald-400" data-testid={`invite-code-${i.code}`}>{i.code}</TableCell>
                <TableCell>{i.label || "—"}</TableCell>
                <TableCell className="font-mono-data">{i.uses} / {i.max_uses === 0 ? "∞" : i.max_uses}</TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(i.created_at)}</TableCell>
                <TableCell><Switch checked={!!i.auto_approve} onCheckedChange={(v) => toggle(i.code, { auto_approve: v })} data-testid={`invite-auto-approve-switch-${i.code}`} /></TableCell>
                <TableCell><Switch checked={i.active} onCheckedChange={(v) => toggle(i.code, { active: v })} data-testid={`invite-active-switch-${i.code}`} /></TableCell>
                <TableCell><Button size="sm" variant="outline" className="gap-1.5" onClick={() => copy(i.code)} data-testid={`invite-copy-${i.code}`}><Copy className="h-3.5 w-3.5" />Copy link</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {list.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground" data-testid="invites-empty">No invite codes yet.</p>}
      </div>
    </div>
  );
}
